import { useState } from 'react';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import Link from 'next/link';
import cx from 'classnames';
import { FunnelIcon } from '@heroicons/react/24/solid';

import { GetServerPageProps } from '../../lib/apollo';
import {
  getServerPageArticles,
  getServerPageArticlesMeta,
  getServerPageArticlesRoutes,
} from './articles.generated';
import { Header } from '../../components/Header';
import { PageMetadata } from '../../components/PageMetadata';
import { Footer } from '../../components/Footer/Footer';
import { Text } from '../../components/Text';
import { CallToActionSection } from '../../components/CallToActionSection';
import { Pagination } from '../../components/Pagination';
import { Column, Row, Section } from '../../components/Section';
import { ArticleCard } from '../../components/ArticleCard';
import { RichText } from '../../lib/copyRendering';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Sidebar } from '../../components/Sidebar';

export default function Articles({
  articles,
  page,
  hasNextPage,
  hasPreviousPage,
  numPages,
  categories,
  category,
  pageMetadata,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const currentCategory = categories.find(
    (allCategories) => allCategories.slug === category,
  );

  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);

  return (
    <>
      <PageMetadata
        pageTitle={`${
          currentCategory?.name ? `${currentCategory.name} | ` : ''
        }${pageMetadata?.seoTitle}`}
        description={
          documentToPlainTextString(currentCategory?.description?.json) ??
          pageMetadata?.seoDescription ??
          ''
        }
      />
      <Header />
      <Section background="BRIGHT_BLUE" className="!pt-6 !pb-4">
        <Row>
          <Column>
            <Text.H1 className="!text-white text-center">
              Healthcare News & Stories
            </Text.H1>
            <Text.H2 className="!text-base !font-semibold !text-white text-center">
              {currentCategory?.name}
            </Text.H2>
          </Column>
        </Row>
      </Section>
      <Section>
        <Row>
          <Column className="grid grid-cols-1 lg:grid-cols-[minmax(min-content,12em),auto] gap-6">
            <Sidebar
              title="Categories"
              open={filtersPanelOpen}
              setOpen={setFiltersPanelOpen}
            >
              <ul role="list">
                {[{ name: 'All', slug: 'all' }, ...categories].map(
                  (sidebarCategory) => (
                    <li key={sidebarCategory.name} className="my-2">
                      <Link
                        href={`/articles/${sidebarCategory.slug}`}
                        className={cx(
                          'px-2 py-1 hover:bg-blue-500 hover:text-white rounded-md',
                          {
                            'bg-gray-400 text-white':
                              category === sidebarCategory.slug,
                          },
                        )}
                      >
                        {sidebarCategory.name}
                      </Link>
                    </li>
                  ),
                )}
              </ul>
            </Sidebar>
            <div>
              <div className="flex justify-end">
                <button
                  className="lg:hidden text-gray-600 "
                  onClick={() => setFiltersPanelOpen(true)}
                >
                  Categories <FunnelIcon className="w-5 m-2 inline-block" />
                </button>
              </div>
              <div className="bg-gray-100 rounded-md p-4 text-gray-700 hidden sm:block">
                {currentCategory?.description ? (
                  <RichText content={currentCategory?.description} />
                ) : (
                  pageMetadata?.seoDescription
                )}
              </div>
              {articles.length === 0 ? (
                <div className="text-center m-8">
                  No articles found in this category
                </div>
              ) : (
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {articles
                    .flatMap((item) => item ?? [])
                    .map((article) => (
                      <ArticleCard article={article} key={article.sys.id} />
                    ))}
                </div>
              )}
            </div>
          </Column>
        </Row>
        <Row>
          <Column>
            <Pagination
              hasPreviousPage={hasPreviousPage}
              page={page}
              numPages={numPages}
              hasNextPage={hasNextPage}
              basePath={`/articles/${category}`}
            />
          </Column>
        </Row>
      </Section>
      <CallToActionSection quoteAction />
      <Footer />
    </>
  );
}

const limit = 24;
const totalPages = (total: number) => Math.ceil(total / limit);

export const getStaticProps: GetStaticProps<{
  articles: NonNullable<
    NonNullable<
      GetServerPageProps<
        typeof getServerPageArticles
      >['data']['articleCollection']
    >['items']
  >;
  page: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  numPages: number;
  categories: NonNullable<
    NonNullable<
      GetServerPageProps<
        typeof getServerPageArticlesMeta
      >['data']['articleCategoryCollection']
    >['items'][0]
  >[];
  category: string;
  pageMetadata:
    | NonNullable<
        GetServerPageProps<
          typeof getServerPageArticlesMeta
        >['data']['pageCollection']
      >['items'][0]
    | undefined;
}> = async ({ params }) => {
  const [categoryParam, pageParam] =
    params?.page && Array.isArray(params.page)
      ? params.page
      : params?.page
      ? [params.page]
      : [];
  const page = pageParam ? Number(pageParam) : 1;
  const category = Array.isArray(categoryParam)
    ? categoryParam[0]
    : categoryParam;
  const {
    props: { data },
  } = await getServerPageArticles({});

  const articles = data.articleCollection?.items;

  if (!articles) {
    throw new Error('Failed to load articles');
  }

  const filteredArticles =
    category === 'all'
      ? articles
      : articles?.filter(
          (item) =>
            item?.categoriesCollection?.items
              .map((item) => item?.slug)
              .includes(category),
        );
  const numPages = totalPages(filteredArticles.length ?? 1);

  const start = limit * (page - 1);
  const end = start + limit;
  const paginatedArticles = filteredArticles.slice(start, end);

  const pageMetadata = await (await getServerPageArticlesMeta({})).props.data;

  const categories =
    pageMetadata.articleCategoryCollection?.items.flatMap(
      (item) => item ?? [],
    ) ?? [];

  return {
    props: {
      articles: paginatedArticles,
      page,
      hasNextPage: page < numPages,
      hasPreviousPage: page > 1,
      numPages,
      categories,
      category,
      pageMetadata: pageMetadata.pageCollection?.items[0],
    },
  };
};

/** Generate paginated pages based on total articles in categories and limit */
export async function getStaticPaths() {
  const {
    props: { data },
  } = await getServerPageArticlesRoutes({});

  if (
    !data.articleCollection ||
    data.articleCollection.items.length !== data.articleCollection.total
  ) {
    throw new Error('Not all articles were loaded');
  }
  if (!data.articleCategoryCollection) {
    throw new Error('Categories not loaded');
  }

  type CategoryCounts = Record<string, number>;

  const categoryZeroCounts =
    data.articleCategoryCollection.items.reduce<CategoryCounts>(
      (categoryCounts, item) => ({
        ...categoryCounts,
        ...(item?.slug && { [item.slug]: 0 }),
      }),
      { all: data.articleCollection.total },
    );

  const categoryCounts = (
    data.articleCollection?.items.flatMap(
      (item) =>
        item?.categoriesCollection?.items.flatMap((item) => item?.slug ?? []) ??
        [],
    ) ?? []
  ).reduce<CategoryCounts>(
    (categoryCounts, slug) => ({
      ...categoryCounts,
      [slug]: (categoryCounts[slug] ?? 0) + 1,
    }),
    categoryZeroCounts,
  );

  const paths = Object.entries(categoryCounts).flatMap(([slug, count]) =>
    [...new Array(totalPages(count || 1))].map((_item, index) => ({
      params: {
        page: index === 0 ? [slug] : [slug, String(index + 1)],
      },
    })),
  );

  return {
    paths,
    fallback: false,
  };
}
